<script lang="ts" setup>
const props = withDefaults(defineProps<{ business: Business, size?: 'xl' | 'lg' | 'sm' | 'xs', buttonColor?: string, variant?: string }>(), { size: 'lg', buttonColor: 'primary', variant: 'solid' })

const messageDialogOpen = ref(false)
const phoneDialogOpen = ref(false)
const isLargeDevice = useMediaQuery('(min-width: 1024px)')
const { copy } = useClipboard()
const toast = useToast()

function openMessage() {
  messageDialogOpen.value = true
}

function callBusiness() {
  const phone = props.business.phone?.trim()

  if (!phone) {
    return
  }

  if (!isLargeDevice.value) {
    window.location.href = `tel:${phone}`
    return
  }

  phoneDialogOpen.value = true
}

async function copyPhoneNumber() {
  const phone = props.business.phone?.trim()

  if (!phone) {
    return
  }

  await copy(phone)
  toast.add({
    title: 'Number copied',
    description: `${phone} copied to clipboard`,
    icon: 'ri:file-copy-line',
  })
}

function openWhatsapp() {
  const phone = props.business.whatsapp?.replace(/\D/g, '')
  const whatsappUrl = `https://wa.me/${phone}`
  window.open(whatsappUrl, '_blank')
}
</script>

<template>
  <div
    v-if="business.website || business.facebook || business.instagram || business.twitter || business.linkedin || business.tiktok || business.youtube || business.phone || business.whatsapp"
    class="flex flex-wrap gap-3 "
  >
    <template v-if="business.website">
      <UTooltip text="Visit Website">
        <UButton
          icon="ri:link"
          aria-label="Visit Website"
          target="_blank"
          :to="business.website"
          :color="buttonColor"
          :variant
          :size
        >
          Website
        </UButton>
      </UTooltip>
    </template>
    <template v-if="business.phone">
      <UTooltip :text="`Call ${business.name}`">
        <UButton
          icon="ri:phone-line"
          :aria-label="`Call ${business.name}`"
          :color="buttonColor"
          :variant
          :size
          @click="callBusiness"
        >
          Call
        </UButton>
      </UTooltip>
    </template>
    <template v-if="business.canMessage">
      <UTooltip :text="`Message ${business.name}`">
        <UButton
          icon="ri:mail-line"
          :aria-label="`Message ${business.name}`"
          :color="buttonColor"
          :variant
          :size
          @click="openMessage"
        >
          Message
        </UButton>
      </UTooltip>
    </template>
    <template v-if="business.whatsapp">
      <UTooltip :text="`Whatsapp ${business.name}`">
        <UButton
          icon="ri:whatsapp-line"
          :aria-label="`Whatsapp ${business.name}`"
          :color="buttonColor"
          :variant
          :size
          @click="openWhatsapp"
        >
          Whatsapp
        </UButton>
      </UTooltip>
    </template>
    <template v-if="business.facebook">
      <UTooltip text="Visit Facebook">
        <UButton
          :color="buttonColor"
          :variant
          :size
          icon="ri:facebook-line"
          aria-label="Visit Facebook"
          target="_blank"
          :to="business.facebook"
        >
          Facebook
        </UButton>
      </UTooltip>
    </template>
    <template v-if="business.instagram">
      <UTooltip text="Visit Instagram">
        <UButton
          :color="buttonColor"
          :variant
          :size
          icon="ri:instagram-line"
          aria-label="Visit Instagram"
          target="_blank"
          :to="business.instagram"
        >
          Instagram
        </UButton>
      </UTooltip>
    </template>
    <template v-if="business.twitter">
      <UTooltip text="Visit Twitter">
        <UButton
          :color="buttonColor"
          :variant
          :size
          icon="ri:twitter-x-line"
          aria-label="Visit X"
          target="_blank"
          :to="business.twitter"
        >
          "X"
        </UButton>
      </UTooltip>
    </template>
    <template v-if="business.linkedin">
      <UTooltip text="Visit LinkedIn">
        <UButton
          :color="buttonColor"
          :variant
          :size
          icon="ri:linkedin-line"
          aria-label="Visit LinkedIn"
          target="_blank"
          :to="business.linkedin"
        >
          LinkedIn
        </UButton>
      </UTooltip>
    </template>
    <template v-if="business.tiktok">
      <UTooltip text="Visit TikTok">
        <UButton
          :color="buttonColor"
          :variant
          :size
          icon="ri:tiktok-line"
          aria-label="Visit TikTok"
          target="_blank"
          :to="business.tiktok"
        >
          TikTok
        </UButton>
      </UTooltip>
    </template>
    <template v-if="business.youtube">
      <UTooltip text="Visit Youtube">
        <UButton
          :color="buttonColor"
          :variant
          :size
          icon="ri:youtube-line"
          aria-label="Visit Youtube"
          target="_blank"
          :to="business.youtube"
        >
          Youtube
        </UButton>
      </UTooltip>
    </template>
    <UModal
      v-model="messageDialogOpen"
      :ui="{ rounded: 'rounded-3xl overflow-clip' }"
      class="contact-form"
    >
      <CollectionBusinessMessage
        :id="business.id"
        :name="business.name"
        @close="messageDialogOpen = false"
      />
    </UModal>
    <UModal
      v-model="phoneDialogOpen"
      :ui="{ rounded: 'rounded-3xl overflow-clip' }"
    >
      <div class="p-6 space-y-4">
        <h3 class="text-xl font-semibold">
          Call {{ business.name }}
        </h3>
        <p class="text-sm text-gray-600 dark:text-gray-300">
          Use your device application if available, or copy the number and call from your phone.
        </p>
        <p class="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {{ business.phone }}
        </p>
        <div class="flex flex-wrap gap-3">
          <UButton
            icon="ri:phone-line"
            :to="`tel:${business.phone}`"
            target="_blank"
            color="primary"
            :variant
            :size
          >
            Call on this device
          </UButton>
          <UButton
            icon="ri:file-copy-line"
            color="primary"
            variant="soft"
            :size
            @click="copyPhoneNumber"
          >
            Copy number
          </UButton>
        </div>
      </div>
    </UModal>
  </div>
</template>
